import { PushNotifications as PluginPush } from '@capacitor/push-notifications';
import axios from '../config/axios';
import { esNativo } from '../config/entorno';

const BANNER_KEY = 'gastos_push_banner_oculto';

// En la app Android (Capacitor) el WebView no soporta Web Push: se usa FCM nativo.
const CANAL_NATIVO = 'vida_avisos';
let listenersNativos = false;
let tokenNativo = '';
let rutaNotificacion = null;
let esperaRegistro = null;

export function usarPushNativo() {
  return esNativo();
}

export function registrarNavegacionPush(fn) {
  rutaNotificacion = fn;
}

function conLimite(promesa, ms = 8000) {
  return Promise.race([
    promesa,
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error(`sin respuesta en ${ms / 1000} s`)), ms);
    })
  ]);
}

// IMPORTANTE: el plugin nunca debe pasar por una promesa (async/await/Promise.race).
// El proxy de Capacitor responde a `then` como si fuera un método nativo y la promesa
// no llega a resolverse jamás. Por eso se devuelve de forma síncrona.
function cargarPlugin() {
  return PluginPush;
}

// Los listeners y el canal se preparan sin bloquear: si el puente nativo tarda,
// no debe quedarse colgado el resto del flujo.
function prepararPluginNativo(PushNotifications) {
  if (listenersNativos) return;
  listenersNativos = true;
  const sinBloqueo = (promesa) => Promise.resolve(promesa).catch((err) => {
    console.warn('Plugin push nativo', err);
  });
  sinBloqueo(PushNotifications.addListener('registration', async ({ value }) => {
    tokenNativo = value;
    try {
      await axios.post('/api/push/fcm', { token: value });
      if (esperaRegistro) esperaRegistro.resolve();
    } catch (err) {
      console.warn('No se pudo guardar el token FCM', err);
      if (esperaRegistro) esperaRegistro.reject(err);
    }
  }));
  sinBloqueo(PushNotifications.addListener('registrationError', (err) => {
    console.warn('Error al registrar FCM', err);
    if (esperaRegistro) {
      esperaRegistro.reject(new Error(`No se pudo registrar el dispositivo en FCM: ${err?.error || 'error desconocido'}`));
    }
  }));
  sinBloqueo(PushNotifications.addListener('pushNotificationActionPerformed', (accion) => {
    const url = accion?.notification?.data?.url;
    if (url && rutaNotificacion) rutaNotificacion(url);
  }));
  sinBloqueo(PushNotifications.createChannel({
    id: CANAL_NATIVO,
    name: 'Avisos de Vida',
    description: 'Gastos compartidos, entrenador, pedidos y alertas',
    importance: 4
  }));
}

async function permisoNativo() {
  const PushNotifications = cargarPlugin();
  const { receive } = await PushNotifications.checkPermissions();
  if (receive === 'granted') return 'granted';
  if (receive === 'denied') return 'denied';
  return 'default';
}

async function activarPushNativo(onPaso = () => {}) {
  onPaso('Consultando el servidor…');
  const { data } = await conLimite(axios.get('/api/push/fcm-estado'), 15000);
  if (!data.enabled) {
    throw new Error('Las notificaciones push no están configuradas en el servidor.');
  }
  onPaso('Cargando el plugin del móvil…');
  const PushNotifications = cargarPlugin();
  prepararPluginNativo(PushNotifications);
  onPaso('Pidiendo permiso a Android…');
  const { receive } = await conLimite(PushNotifications.requestPermissions(), 60000);
  if (receive !== 'granted') {
    throw new Error('Has bloqueado las notificaciones. Actívalas en los ajustes de Android para Vida.');
  }
  const registrado = new Promise((resolve, reject) => {
    esperaRegistro = { resolve, reject };
  });
  try {
    onPaso('Registrando el dispositivo en FCM…');
    // register() puede no resolverse aunque el token llegue por el evento 'registration',
    // así que se espera al evento y no a la promesa.
    Promise.resolve(PushNotifications.register()).catch((err) => {
      if (esperaRegistro) esperaRegistro.reject(err instanceof Error ? err : new Error(String(err?.message || err)));
    });
    await conLimite(registrado, 25000);
  } catch (err) {
    // Si el servidor ya tiene este dispositivo suscrito, la activación sigue siendo válida.
    let suscrito = false;
    try {
      const est = await conLimite(axios.get('/api/push/estado'), 10000);
      suscrito = !!est.data.suscrito;
    } catch {
      /* ignore */
    }
    if (!suscrito) throw err;
  } finally {
    esperaRegistro = null;
  }
  return { ok: true };
}

export function pushSoportado() {
  if (usarPushNativo()) return true;
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;
}

export function esIos() {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
}

async function registration() {
  if (!('serviceWorker' in navigator)) return null;
  const existente = await navigator.serviceWorker.getRegistration('/');
  if (!existente) {
    await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  }
  // ready no se resuelve nunca si el service worker no llega a activarse
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error('El service worker no está activo. Cierra y vuelve a abrir la app e inténtalo de nuevo.')), 10000);
    })
  ]);
}

export async function obtenerEstadoPush() {
  if (usarPushNativo()) {
    let enabled = false;
    let suscritoServidor = false;
    let permission = 'default';
    try {
      const { data } = await conLimite(axios.get('/api/push/fcm-estado'), 10000);
      enabled = !!data.enabled;
    } catch {
      /* se reintenta al pulsar Activar */
    }
    try {
      permission = await conLimite(permisoNativo());
    } catch {
      /* sin respuesta del plugin: se deja en default */
    }
    if (permission === 'granted') {
      try {
        const est = await conLimite(axios.get('/api/push/estado'), 10000);
        suscritoServidor = !!est.data.suscrito;
      } catch {
        /* ignore */
      }
    }
    return { supported: true, permission, enabled, publicKey: null, suscritoServidor, ios: false };
  }
  const supported = pushSoportado();
  const ios = esIos();
  const permission = supported ? Notification.permission : 'unsupported';
  let enabled = false;
  let publicKey = null;
  let suscritoServidor = false;
  try {
    const { data } = await axios.get('/api/push/vapid-public-key');
    enabled = !!data.enabled;
    publicKey = data.publicKey || null;
  } catch {
    enabled = false;
  }
  if (supported && permission === 'granted') {
    try {
      const { data } = await axios.get('/api/push/estado');
      suscritoServidor = !!data.suscrito;
    } catch {
      /* ignore */
    }
  }
  return { supported, permission, enabled, publicKey, suscritoServidor, ios };
}

export async function activarPush(onPaso) {
  if (usarPushNativo()) return activarPushNativo(onPaso);
  if (!pushSoportado()) {
    throw new Error(esIos()
      ? 'En iPhone, Chrome no permite notificaciones web. Usa el correo o instala la app desde Safari.'
      : 'Este navegador no admite notificaciones push.');
  }
  const { data } = await axios.get('/api/push/vapid-public-key');
  if (!data.enabled || !data.publicKey) {
    throw new Error('Las notificaciones push no están configuradas en el servidor.');
  }
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Has bloqueado las notificaciones. Actívalas en los ajustes de Chrome para este sitio.');
  }
  const reg = await registration();
  if (!reg) throw new Error('No se pudo registrar el service worker.');
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.publicKey)
    });
  }
  await axios.post('/api/push/subscribe', sub.toJSON());
  return { ok: true };
}

export async function desactivarPush() {
  if (usarPushNativo()) {
    try {
      await axios.delete('/api/push/fcm', { data: tokenNativo ? { token: tokenNativo } : {} });
    } catch {
      /* ignore */
    }
    try {
      const PushNotifications = cargarPlugin();
      await PushNotifications.unregister();
    } catch {
      /* ignore */
    }
    return;
  }
  const reg = await registration();
  let endpoint = null;
  if (reg) {
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      endpoint = sub.endpoint;
      await sub.unsubscribe();
    }
  }
  try {
    await axios.delete('/api/push/subscribe', { data: endpoint ? { endpoint } : {} });
  } catch {
    /* ignore */
  }
}

export async function sincronizarPushSiActivo() {
  if (usarPushNativo()) {
    try {
      const { data } = await axios.get('/api/push/fcm-estado');
      if (!data.enabled) return;
      if (await permisoNativo() !== 'granted') return;
      const PushNotifications = cargarPlugin();
      prepararPluginNativo(PushNotifications);
      await conLimite(PushNotifications.register(), 15000);
    } catch (err) {
      console.warn('No se pudo sincronizar FCM', err);
    }
    return;
  }
  if (!pushSoportado()) return;
  if (Notification.permission !== 'granted') return;
  try {
    const { data } = await axios.get('/api/push/vapid-public-key');
    if (!data.enabled || !data.publicKey) return;
    const reg = await registration();
    if (!reg) return;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.publicKey)
      });
    }
    await axios.post('/api/push/subscribe', sub.toJSON());
  } catch (err) {
    console.warn('No se pudo sincronizar la suscripción push', err);
  }
}

export async function enviarPushDePrueba(retraso = 0) {
  const { data } = await axios.post('/api/push/probar', retraso ? { retraso } : {});
  return data;
}

export function bannerPushOculto() {
  try {
    return localStorage.getItem(BANNER_KEY) === '1';
  } catch {
    return false;
  }
}

export function ocultarBannerPush() {
  try {
    localStorage.setItem(BANNER_KEY, '1');
  } catch {
    /* ignore */
  }
}
